/**
 * OSM road ribbons (issue #138 render; issue #141 build-time drape). The full
 * tarmac road network for the AOI, drawn as one merged tarmac ribbon mesh built
 * ONCE at construction from build-time-baked ground heights
 * (data/config/roads_draped.json, produced by scripts/10_bake_road_drape.py) —
 * NOT draped at runtime.
 *
 * #138 sampled ground height under every vertex on the render loop, budgeted per
 * tick and shared with walls/water, so across ~418 roads the whole network took
 * MINUTES to fill in and janked the renderer while it worked. #141 bakes those
 * heights against the committed LOD1 TIN tiles at build time, so the finished
 * mesh is complete after the constructor returns and renders on the first
 * frame(s) — zero runtime sampling, no per-tick budget (repo perf rule #17).
 *
 * Purely illustrative scene furniture, exactly like routeRibbon.ts: no bearing
 * on any LOS result (hard constraint #1, guarded by roadRibbons.guard.test.ts).
 * No invented elevation (hard constraint #6): a densified vertex the bake could
 * not ground (no covering tile, or a downward ray that missed the TIN) carries
 * a null height, and any ribbon segment touching a null vertex is skipped —
 * never given a guessed height. Verges use the centreline baked height (a flat
 * cross-section), the accepted simplification for a 2.5–4 m ribbon.
 *
 * Reuses routeRibbon.ts's pure geometry builders (buildCrossSections,
 * buildRibbonTriangles) rather than reimplementing the cross-section/triangle
 * maths. Every road merges into ONE mesh (one draw call, issue #17). Lifted
 * slightly BELOW the route ribbon (ROAD_RIBBON_LIFT_M < RIBBON_LIFT_M) so where
 * a road coincides with the route the route ribbon renders deterministically on
 * top instead of z-fighting.
 *
 * Per-class styling (width + colour only): `highway=service` tracks (farm
 * access / field entrances / driveways) render narrower (SERVICE_WIDTH_M) and
 * dirt-brown; every public class (primary/secondary/tertiary/unclassified/
 * trunk, and any unknown future class) renders at the full tarmac width and
 * colour. Colour is applied as PER-VERTEX colours on a DEDICATED material
 * (ROAD_RIBBON_MATERIAL, vertexColors enabled), so the whole network stays a
 * single merged mesh and the shared route RIBBON_MATERIAL is never mutated.
 */
import { BufferAttribute, BufferGeometry, Color, DoubleSide, Mesh, MeshStandardMaterial } from 'three';
import { bngToLocal, type LocalXZ } from '../core/coordinates';
import type { DrapedRoad, Road, RoadsDrapedFile } from '../core/manifest';
import {
  RIBBON_WIDTH_M,
  buildCrossSections,
  buildRibbonTriangles,
  type ResolvedRibbonVertex,
} from './routeRibbon';

/** Lift (m) below the route ribbon's RIBBON_LIFT_M (0.15), so where an OSM road
 * coincides with the route the route ribbon renders on top rather than
 * z-fighting two coplanar lifted ribbons. */
export const ROAD_RIBBON_LIFT_M = 0.13;

/** Ribbon width (m) for `highway=service` tracks — narrower than the public
 * carriageway width so a farm access / field entrance is not drawn as a full
 * 4 m lane. */
export const SERVICE_WIDTH_M = 2.5;

/** Dark tarmac (public roads + the main route ribbon share this exact tone). */
export const TARMAC_COLOR_HEX = 0x2a2d33;
/** Dirt-brown for service tracks. Chosen to stay clearly browner and lighter
 * than the near-black tarmac in BOTH Natural and Stylised terrain-shading
 * presets (issue #138). */
export const SERVICE_COLOR_HEX = 0x6b5a42;

/** Ribbon width (m) for a road of the given class — pure, three.js-free, so it
 * is unit-testable in Node. `service` is the only narrowed class; every other
 * class (including any unrecognised future `highway` value) gets the public
 * tarmac width. */
export function roadWidthForClass(roadClass: Road['road_class']): number {
  return roadClass === 'service' ? SERVICE_WIDTH_M : RIBBON_WIDTH_M;
}

/** Ribbon colour (hex) for a road of the given class — pure, three.js-free.
 * `service` → dirt-brown; every other class (incl. unknown) → dark tarmac. */
export function roadColorForClass(roadClass: Road['road_class']): number {
  return roadClass === 'service' ? SERVICE_COLOR_HEX : TARMAC_COLOR_HEX;
}

/** Dedicated OSM-road material: cloned from RIBBON_MATERIAL's tarmac params but
 * white-based with vertexColors enabled, so the per-vertex colour alone paints
 * each surface (tarmac vs dirt) and the shared route RIBBON_MATERIAL is never
 * mutated. */
export const ROAD_RIBBON_MATERIAL = new MeshStandardMaterial({
  color: 0xffffff,
  vertexColors: true,
  roughness: 0.9,
  metalness: 0.0,
  side: DoubleSide,
  polygonOffset: true,
  polygonOffsetFactor: -1,
  polygonOffsetUnits: -1,
});

/**
 * Resolves one baked road's densified centreline into ribbon cross-section
 * vertices, ready for buildRibbonTriangles. Pure and three.js-free (Node
 * testable). Each baked `[easting, northing, height]` maps to local x/z via
 * bngToLocal (the single source of truth for placement); the baked ground
 * height (+ liftM) becomes the local y for BOTH verges (flat cross-section). A
 * null baked height yields an unresolved cross-section, so buildRibbonTriangles
 * drops any segment touching it (hard constraint #6 — no invented elevation).
 */
export function resolveDrapedRoad(road: DrapedRoad, liftM = ROAD_RIBBON_LIFT_M): ResolvedRibbonVertex[] {
  const points: LocalXZ[] = [];
  const heights: (number | null)[] = [];
  for (const [easting, northing, height] of road.draped_polyline) {
    points.push(bngToLocal(easting, northing));
    heights.push(height);
  }
  const sections = buildCrossSections(points, roadWidthForClass(road.road_class));
  return sections.map((s, i) => {
    const h = heights[i];
    if (h === null || h === undefined) return { left: null, right: null };
    const y = h + liftM;
    return {
      left: { x: s.left.x, y, z: s.left.z },
      right: { x: s.right.x, y, z: s.right.z },
    };
  });
}

export interface RoadRibbonsOptions {
  liftM?: number;
}

/**
 * Three.js wrapper: builds the full merged tarmac ribbon mesh for every road in
 * a baked RoadsDrapedFile AT CONSTRUCTION — one mesh, one draw call — with no
 * runtime ground sampling and no update() tick. Construct once with the loaded
 * draped file, add `.mesh` to the scene; the geometry is complete immediately.
 */
export class RoadRibbonsLayer {
  readonly mesh: Mesh;

  constructor(file: RoadsDrapedFile, options: RoadRibbonsOptions = {}) {
    const liftM = options.liftM ?? ROAD_RIBBON_LIFT_M;

    const posParts: Float32Array[] = [];
    const colParts: Float32Array[] = [];
    let total = 0;
    for (const road of file.roads) {
      const triangles = buildRibbonTriangles(resolveDrapedRoad(road, liftM));
      if (triangles.length === 0) continue;
      posParts.push(triangles);
      // One colour triple per emitted vertex (3 position floats each): a whole
      // road is a single class, so every vertex of its triangles gets the same
      // tarmac/dirt colour.
      const c = new Color(roadColorForClass(road.road_class));
      const colors = new Float32Array(triangles.length);
      for (let i = 0; i < triangles.length; i += 3) {
        colors[i] = c.r;
        colors[i + 1] = c.g;
        colors[i + 2] = c.b;
      }
      colParts.push(colors);
      total += triangles.length;
    }

    const geometry = new BufferGeometry();
    if (total > 0) {
      const mergedPos = new Float32Array(total);
      const mergedCol = new Float32Array(total);
      let offset = 0;
      for (let p = 0; p < posParts.length; p++) {
        mergedPos.set(posParts[p], offset);
        mergedCol.set(colParts[p], offset);
        offset += posParts[p].length;
      }
      geometry.setAttribute('position', new BufferAttribute(mergedPos, 3));
      geometry.setAttribute('color', new BufferAttribute(mergedCol, 3));
      geometry.computeVertexNormals();
      geometry.computeBoundingSphere();
    }

    this.mesh = new Mesh(geometry, ROAD_RIBBON_MATERIAL);
    this.mesh.name = 'road-ribbons';
    this.mesh.receiveShadow = true;
    this.mesh.frustumCulled = false;
  }

  /** Emitted vertex count of the merged mesh (0 before any road resolves),
   * exposed for tests/diagnostics. */
  get vertexCount(): number {
    return this.mesh.geometry.getAttribute('position')?.count ?? 0;
  }
}
